export const PAID_ACCESS_EVENT = 'cleanlist:paid-access-required';
export const PAID_ACCESS_RETURN_KEY = 'cleanlist:paid-access-return';

export interface PaidAccessNotice {
  message: string;
  requiredPlan?: string;
  /** The action the plan refused, e.g. `clu_writing`; absent for a launch. */
  feature?: string;
  pending?: boolean;
  paymentRequired?: boolean;
}

export function requestPaidAccess(notice: PaidAccessNotice) {
  if (typeof window === 'undefined') return;
  window.dispatchEvent(new CustomEvent(PAID_ACCESS_EVENT, { detail: notice }));
}

/** Read only recognized server errors. Never infer that payment succeeded. */
export function paidAccessNotice(error: unknown): PaidAccessNotice | null {
  if (!error || typeof error !== 'object') return null;
  const data = (error as { data?: unknown }).data;
  if (!data || typeof data !== 'object') return null;
  const envelope = (data as { error?: unknown }).error;
  const canonical =
    envelope && typeof envelope === 'object'
      ? (envelope as Record<string, unknown>)
      : undefined;
  const detail = canonical?.details ?? (data as { detail?: unknown }).detail;
  if (!detail || typeof detail !== 'object') return null;
  const payload = detail as Record<string, unknown>;
  // The search admission 402 names its state under `error`, not `code`.
  const code = String(payload.code ?? payload.error);
  if (
    ![
      'paid_launch_required',
      'trial_upgrade_required',
      'trial_conversion_pending',
      'trial_payment_required',
    ].includes(code)
  )
    return null;
  return {
    // With no message from the raiser, the envelope repeats its own code
    // (e.g. PERMISSION_DENIED), which is not copy to show a customer.
    message:
      typeof canonical?.message === 'string' &&
      canonical.message !== canonical.code
        ? canonical.message
        : typeof payload.message === 'string'
          ? payload.message
          : code === 'trial_conversion_pending'
            ? 'Your paid plan is being confirmed. Retry shortly.'
            : 'Choose a paid plan to continue.',
    requiredPlan:
      typeof payload.required_plan === 'string'
        ? payload.required_plan
        : undefined,
    pending: code === 'trial_conversion_pending',
    ...(typeof payload.feature === 'string'
      ? { feature: payload.feature }
      : {}),
    ...(code === 'trial_payment_required' ? { paymentRequired: true } : {}),
  };
}
